/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { Frame } from '@ouster/datasources';

import { openDB, IDBPDatabase, DBSchema } from 'idb';

interface CachedFrame {
  recordingId: string;
  frameIndex: number;
  frame: Frame;
  timestamp: number;
  accessCount: number;
}

interface InvalidFrame {
  recordingId: string;
  frameIndex: number;
  timestamp: number;
  errorType: string;
}

const DB_NAME = 'ouster-frame-cache';
const STORE_NAME = 'frames';
const INVALID_STORE_NAME = 'invalid-frames';

const MAX_TOTAL_FRAMES_GLOBAL = 4000; // e.g., ~8GB total limit
const DELETE_BATCH_SIZE = 100; // Delete this many at once to avoid thrashing

const MAX_TOTAL_CACHE_SIZE_GB = 10; // Total cache limit across all recordings: 10GB
const AVERAGE_FRAME_SIZE_MB = 2; // Conservative estimate per frame

interface FrameCacheDB extends DBSchema {
  frames: {
    key: [string, number];
    value: CachedFrame;
    indexes: { timestamp: number; recordingId: string };
  };
  'invalid-frames': {
    key: [string, number];
    value: InvalidFrame;
    indexes: { timestamp: number; recordingId: string };
  };
}

let db: IDBPDatabase<FrameCacheDB> | null = null;

// Runtime toggle to allow quickly disabling IndexedDB caching for debugging
let indexedDbCachingEnabled = true;

/**
 * Enable or disable IndexedDB frame caching at runtime. When disabled,
 * cache reads/writes become no-ops and the DB handle is released.
 *
 * @param enabled - true to enable caching, false to disable
 */
export function setIndexedDbCachingEnabled(enabled: boolean): void {
  indexedDbCachingEnabled = !!enabled;
  if (!indexedDbCachingEnabled) {
    // Release handles to avoid keeping ArrayBuffers alive
    try {
      releaseDbHandles();
    } catch (err) {
      console.warn('Error releasing DB handles after disabling cache:', err);
    }
  }
}

async function initializeDB(): Promise<IDBPDatabase<FrameCacheDB>> {
  if (db) return db;

  db = await openDB<FrameCacheDB>(DB_NAME, 2, {
    // Bump version for new store
    upgrade(database) {
      // Create frames store
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, {
          keyPath: ['recordingId', 'frameIndex'],
        });
        store.createIndex('timestamp', 'timestamp', { unique: false });
        store.createIndex('recordingId', 'recordingId', { unique: false });
      }

      // Create invalid frames store (new in version 2)
      if (!database.objectStoreNames.contains(INVALID_STORE_NAME)) {
        const invalidStore = database.createObjectStore(INVALID_STORE_NAME, {
          keyPath: ['recordingId', 'frameIndex'],
        });
        invalidStore.createIndex('timestamp', 'timestamp', { unique: false });
        invalidStore.createIndex('recordingId', 'recordingId', {
          unique: false,
        });
      }
    },
  });
  // Clean up old entries on startup
  cleanupOldEntries().catch(console.warn);

  // Check total cache size and cleanup if needed
  checkAndCleanupTotalCacheSize().catch(console.warn);

  return db;
}

function getKey(recordingId: string, frameIndex: number): [string, number] {
  return [recordingId, frameIndex];
}

/**
 * Check if a frame has been marked as invalid due to previous errors.
 *
 * @param recordingId - The ID of the recording
 * @param frameIndex - The index of the frame to check
 * @returns Promise that resolves to true if the frame is marked invalid
 */
export async function isFrameMarkedInvalid(
  recordingId: string,
  frameIndex: number
): Promise<boolean> {
  if (!indexedDbCachingEnabled) return false;
  try {
    const database = await initializeDB();
    const invalidFrame = await database.get(
      INVALID_STORE_NAME,
      getKey(recordingId, frameIndex)
    );

    return !!invalidFrame;
  } catch (error) {
    console.warn(`Failed to check invalid frame ${frameIndex}:`, error);
    return false; // If we can't check, allow the attempt
  }
}

/**
 * Mark a frame as invalid to avoid repeated failed attempts to load it.
 *
 * @param recordingId - The ID of the recording
 * @param frameIndex - The index of the frame to mark as invalid
 * @param errorType - The type of error that occurred
 */
export async function markFrameAsInvalid(
  recordingId: string,
  frameIndex: number,
  errorType: string
): Promise<void> {
  if (!indexedDbCachingEnabled) return;
  try {
    const database = await initializeDB();

    // Only skip marking as invalid for pure network connectivity issues
    // Still mark as invalid if the frame data itself is corrupted or missing from source
    const isPureNetworkError =
      errorType.includes('Failed to fetch') ||
      errorType.includes('timeout') ||
      errorType.includes('NetworkError');

    if (isPureNetworkError) {
      return; // Don't mark pure network connectivity errors as invalid
    }

    // Mark as invalid for:
    // - "datasource - Could not get frame X" (frame missing from OSF)
    // - "Frame X unavailable: network failed and not in cache" (confirmed missing)
    // - Parsing/structure errors
    const invalidFrame: InvalidFrame = {
      recordingId,
      frameIndex,
      timestamp: Date.now(),
      errorType,
    };

    await database.put(INVALID_STORE_NAME, invalidFrame);
  } catch (error) {
    console.warn(`Failed to mark frame ${frameIndex} as invalid:`, error);
  }
}

/**
 * Retrieve a cached frame from storage.
 *
 * @param recordingId - The ID of the recording
 * @param frameIndex - The index of the frame to retrieve
 * @returns Promise that resolves to the cached frame or null if not found
 */
export async function getCachedFrame(
  recordingId: string,
  frameIndex: number
): Promise<Frame | null> {
  if (!indexedDbCachingEnabled) return null;
  try {
    const database = await initializeDB();
    const cachedFrame = await database.get(
      STORE_NAME,
      getKey(recordingId, frameIndex)
    );

    if (cachedFrame && cachedFrame.frame) {
      // Validate the cached frame before returning it (WASM frame structure)
      const frameObj = cachedFrame.frame as unknown as Record<string, unknown>;
      const hasWasmStructure = Array.isArray(frameObj.dataMessages);

      if (!hasWasmStructure) {
        // Remove the corrupted frame from cache
        database
          .delete(STORE_NAME, getKey(recordingId, frameIndex))
          .catch(console.warn);
        return null;
      }

      // Update access info in background
      updateAccessInfo(recordingId, frameIndex).catch(console.warn);
      return cachedFrame.frame;
    }

    return null;
  } catch (error) {
    console.warn(`Failed to get frame ${frameIndex} from cache:`, error);
    return null;
  }
}

/**
 * Store a frame in the cache.
 *
 * @param recordingId - The ID of the recording
 * @param frameIndex - The index of the frame to store
 * @param frame - The frame data to cache
 */
export async function storeCachedFrame(
  recordingId: string,
  frameIndex: number,
  frame: Frame
): Promise<void> {
  if (!indexedDbCachingEnabled) return;
  try {
    // Validate frame before attempting to cache
    if (!frame || typeof frame !== 'object') {
      return;
    }

    // Basic frame structure validation (WASM frame structure)
    const frameObj = frame as unknown as Record<string, unknown>;
    if (!Array.isArray(frameObj.dataMessages)) {
      return;
    }

    const database = await initializeDB();

    // Check and cleanup if needed
    if (Math.random() < 0.05) {
      await ensureGlobalSpace();
    }

    const cachedFrame: CachedFrame = {
      recordingId,
      frameIndex,
      frame,
      timestamp: Date.now(),
      accessCount: 1,
    };

    await database.put(STORE_NAME, cachedFrame);

    // Update in-memory indices cache
    updateCachedIndices(recordingId, frameIndex);

    // Remove from invalid cache if it was previously marked as invalid
    await database.delete(INVALID_STORE_NAME, getKey(recordingId, frameIndex));
  } catch (error) {
    // Don't let cache failures break the main functionality
    console.warn(`Failed to store frame ${frameIndex} in cache:`, error);
  }
}

async function updateAccessInfo(
  recordingId: string,
  frameIndex: number
): Promise<void> {
  if (!indexedDbCachingEnabled) return;
  try {
    const database = await initializeDB();
    const cachedFrame = await database.get(
      STORE_NAME,
      getKey(recordingId, frameIndex)
    );

    if (cachedFrame) {
      cachedFrame.timestamp = Date.now();
      cachedFrame.accessCount += 1;
      await database.put(STORE_NAME, cachedFrame);
    }
  } catch (error) {
    console.warn('Failed to update access info:', error);
  }
}

/**
 * Deletes the absolute oldest frames in the database.
 * Optimized to strictly avoid loading frame data (blobs) into memory.
 */
async function evictOldestFramesGlobal(countToRemove: number): Promise<void> {
  if (!indexedDbCachingEnabled) return;
  try {
    const database = await initializeDB();
    const tx = database.transaction(STORE_NAME, 'readwrite');
    const index = tx.store.index('timestamp'); // Oldest timestamps first

    let removedCount = 0;

    // Iterate the index.
    // IMPORTANT: specific key cursor prevents loading the 'value' (Blob)
    for await (const cursor of index.iterate()) {
      if (removedCount >= countToRemove) break;

      // In your schema, KeyPath is ['recordingId', 'frameIndex']
      // So cursor.primaryKey is an array: [recordingId, frameIndex]
      const [recId, fIdx] = cursor.primaryKey as [string, number];

      // 1. Delete from DB
      await cursor.delete();

      // 2. Update in-memory cache
      // We got the recordingId from the key, so we didn't need to load the blob!
      const cachedIndices = cachedIndicesCache.get(recId);
      if (cachedIndices) {
        cachedIndices.delete(fIdx);
      }

      removedCount += 1;
    }

    await tx.done;
  } catch (error) {
    console.warn('Failed to evict oldest frames:', error);
  }
}

/**
 * Checks total database usage and removes the oldest frames globally
 * regardless of which recording they belong to.
 */
async function ensureGlobalSpace(): Promise<void> {
  if (!indexedDbCachingEnabled) return;
  try {
    const database = await initializeDB();

    // Check total count across ALL recordings
    const totalCount = await database.count(STORE_NAME);

    if (totalCount > MAX_TOTAL_FRAMES_GLOBAL) {
      // Calculate how many to remove to get a bit of breathing room
      const countToRemove =
        totalCount - MAX_TOTAL_FRAMES_GLOBAL + DELETE_BATCH_SIZE;
      await evictOldestFramesGlobal(countToRemove);
    }
  } catch (error) {
    console.warn('Failed to ensure global cache space:', error);
  }
}

/**
 * Detect whether persistent storage/capacity looks available for caching.
 * Returns true when it's reasonable to attempt IndexedDB caching, false
 * when we should avoid it (e.g. private mode, very low quota).
 */
export async function detectStorageForCaching(): Promise<boolean> {
  try {
    if (!('storage' in navigator)) return true; // conservative: allow caching

    // If storage.persisted() exists we can check if we already have persistence
    // (granted by the user previously).
    // If persisted is true, prefer caching.
    // If not persisted, we can call estimate() and optionally request persist().
    // Be conservative: if estimate shows very low quota, disable caching.
    const nav: Navigator = navigator;

    if (typeof nav.storage?.persisted === 'function') {
      try {
        const already = await nav.storage.persisted();
        if (already) return true;
      } catch (e) {
        // ignore and continue to estimate
      }
    }

    if (typeof nav.storage?.estimate === 'function') {
      try {
        const est = await nav.storage.estimate();
        const quota = typeof est.quota === 'number' ? est.quota : undefined;

        // If quota is extremely small (< 10 MB) likely in private mode or restricted.
        if (quota !== undefined && quota < 10 * 1024 * 1024) {
          return false;
        }
      } catch (e) {
        // If estimate fails, fall through to try persisted/request
      }
    }

    // Try to request persistence if available. If the request is denied we
    // should avoid relying on persistent caching.
    if (typeof nav.storage?.persist === 'function') {
      try {
        const ok = await nav.storage.persist();
        return !!ok;
      } catch (e) {
        return false;
      }
    }

    // Absent any signals, allow caching by default but be permissive.
    return true;
  } catch (err) {
    console.warn(
      'Storage detection failed, defaulting to enable caching:',
      err
    );
    return true;
  }
}

async function checkAndCleanupTotalCacheSize(): Promise<void> {
  if (!indexedDbCachingEnabled) return;
  try {
    const database = await initializeDB();

    let estimatedSizeGB = 0;
    let totalFrameCount = 0;
    let sizeSource = 'estimated';

    // Use navigator.storage.estimate() if available for more accurate size
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      try {
        const storageEstimate = await navigator.storage.estimate();
        if (storageEstimate.usage) {
          estimatedSizeGB = storageEstimate.usage / 1024 ** 3;
          sizeSource = 'actual';
        }
      } catch (error) {
        console.warn(
          'Failed to get storage estimate, using frame count estimate:',
          error
        );
      }
    }

    // Fallback: if navigator.storage.estimate() failed or isn't available
    if (sizeSource === 'estimated') {
      totalFrameCount = await database.count(STORE_NAME);
      estimatedSizeGB = (totalFrameCount * AVERAGE_FRAME_SIZE_MB) / 1024;
    }

    if (estimatedSizeGB > MAX_TOTAL_CACHE_SIZE_GB) {
      console.warn(
        `Cache size (${estimatedSizeGB.toFixed(
          1
        )}GB) exceeds limit (${MAX_TOTAL_CACHE_SIZE_GB}GB), cleaning up oldest recordings`
      );

      // Get all recordings and their frame counts
      const recordingStats = await getRecordingStats(database);

      // Sort by oldest access time
      recordingStats.sort((a, b) => a.oldestTimestamp - b.oldestTimestamp);

      // Remove entire recordings starting with oldest until we're under limit
      let currentSizeGB = estimatedSizeGB;
      for (const recording of recordingStats) {
        if (currentSizeGB <= MAX_TOTAL_CACHE_SIZE_GB * 0.8) break; // Stop at 80% of limit
        await clearCacheForRecording(recording.recordingId, true); // Clear invalid frames too for total cleanup

        // Update size estimate after cleanup
        if (
          sizeSource === 'actual' &&
          'storage' in navigator &&
          'estimate' in navigator.storage
        ) {
          try {
            const newEstimate = await navigator.storage.estimate();
            if (newEstimate.usage) {
              currentSizeGB = newEstimate.usage / 1024 ** 3;
            }
          } catch (error) {
            // Fall back to calculation
            currentSizeGB -=
              (recording.frameCount * AVERAGE_FRAME_SIZE_MB) / 1024;
          }
        } else {
          currentSizeGB -=
            (recording.frameCount * AVERAGE_FRAME_SIZE_MB) / 1024;
        }
      }
    }
  } catch (error) {
    console.warn('Failed to check total cache size:', error);
  }
}

async function getRecordingStats(database: IDBPDatabase<FrameCacheDB>): Promise<
  Array<{
    recordingId: string;
    frameCount: number;
    oldestTimestamp: number;
  }>
> {
  const stats = new Map<
    string,
    { frameCount: number; oldestTimestamp: number }
  >();

  const tx = database.transaction(STORE_NAME, 'readonly');

  // We cannot easily use cursor.value (too heavy) or key-only iteration (no timestamp).
  // We iterate the timestamp index.
  const index = tx.store.index('timestamp');

  // To avoid iterating 1000s of items, we really only need an estimate or just
  // checking distinct recordingIds.
  // However, for correctness, we'll iterate. Note that this iteration IS heavy
  // on IO, but won't crash RAM if we don't store results.
  for await (const cursor of index.iterate()) {
    const val = cursor.value; // Deserializes, but GC collects immediately.
    const { recordingId, timestamp } = val;

    const existing = stats.get(recordingId);
    if (!existing) {
      stats.set(recordingId, {
        frameCount: 1,
        oldestTimestamp: timestamp,
      });
    } else {
      existing.frameCount += 1;
      // Index is sorted, so first time we see an ID, it's the oldest.
      // But we just increment count here.
    }
  }
  await tx.done;

  return Array.from(stats.entries()).map(([recordingId, data]) => ({
    recordingId,
    ...data,
  }));
}

async function cleanupOldEntries(): Promise<void> {
  try {
    const database = await initializeDB();
    const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;

    // Clean up old frames (1 week)
    const framesTx = database.transaction(STORE_NAME, 'readwrite');
    for await (const cursor of framesTx.store.iterate()) {
      if (cursor.value && cursor.value.timestamp < oneWeekAgo) {
        await cursor.delete();
      }
    }
    await framesTx.done;

    // Clean up old invalid frames (30 days - keep longer since they're tiny)
    const invalidTx = database.transaction(INVALID_STORE_NAME, 'readwrite');
    for await (const cursor of invalidTx.store.iterate()) {
      if (cursor.value && cursor.value.timestamp < thirtyDaysAgo) {
        await cursor.delete();
      }
    }
    await invalidTx.done;
  } catch (error) {
    console.warn('Failed to cleanup old entries:', error);
  }
}

/**
 * Clear all invalid frame markings for a specific recording.
 *
 * @param recordingId - The ID of the recording to clear invalid frames for
 */
export async function clearInvalidFramesForRecording(
  recordingId: string
): Promise<void> {
  if (!indexedDbCachingEnabled) return;
  try {
    const database = await initializeDB();
    const range = IDBKeyRange.bound(
      [recordingId, 0],
      [recordingId, Number.MAX_SAFE_INTEGER]
    );

    const invalidTx = database.transaction(INVALID_STORE_NAME, 'readwrite');
    for await (const cursor of invalidTx.store.iterate(range)) {
      if (cursor.value) {
        await cursor.delete();
      }
    }
    await invalidTx.done;
  } catch (error) {
    console.warn('Failed to clear invalid frames for recording:', error);
  }
}

/**
 * Clear all cached frames for a specific recording.
 *
 * @param recordingId - The ID of the recording to clear cache for
 * @param clearInvalidFrames - Whether to also clear invalid frame markings (default: true)
 */
export async function clearCacheForRecording(
  recordingId: string,
  clearInvalidFrames = true // Changed default to true to clear stale invalid markings
): Promise<void> {
  if (!indexedDbCachingEnabled) return;
  try {
    const database = await initializeDB();
    const range = IDBKeyRange.bound(
      [recordingId, 0],
      [recordingId, Number.MAX_SAFE_INTEGER]
    );

    // Clear regular cache (large frame data)
    const framesTx = database.transaction(STORE_NAME, 'readwrite');
    for await (const cursor of framesTx.store.iterate(range)) {
      if (cursor.value) {
        await cursor.delete();
      }
    }
    await framesTx.done;

    // Optionally clear invalid frames (for total cleanup)
    if (clearInvalidFrames) {
      const invalidTx = database.transaction(INVALID_STORE_NAME, 'readwrite');
      for await (const cursor of invalidTx.store.iterate(range)) {
        if (cursor.value) {
          await cursor.delete();
        }
      }
      await invalidTx.done;
    }

    // Clear in-memory indices cache
    clearCachedIndices(recordingId);
  } catch (error) {
    console.warn('Failed to clear cache for recording:', error);
  }
}

/**
 * Clear all cached frames and invalid frame markings.
 */
export async function clearAllCache(): Promise<void> {
  if (!indexedDbCachingEnabled) return;
  try {
    const database = await initializeDB();
    await database.clear(STORE_NAME);
    await database.clear(INVALID_STORE_NAME);
  } catch (error) {
    console.warn('Failed to clear all cache:', error);
  }
}

/**
 * Test whether frame caching is working correctly by performing a round-trip test.
 *
 * @param frame - The frame to test caching with
 * @returns Promise that resolves to true if caching works, false otherwise
 */
export async function testFrameCaching(frame: Frame): Promise<boolean> {
  if (!indexedDbCachingEnabled) return false;
  try {
    // First validate the input frame
    if (!frame || typeof frame !== 'object') {
      console.warn('Test frame is invalid');
      return false;
    }
    // Check for WASM frame structure (dataMessages)
    const frameObj = frame as unknown as Record<string, unknown>;
    const isWasmFrame = Array.isArray(frameObj.dataMessages);

    if (!isWasmFrame) {
      console.warn('Test frame has corrupted structure - missing dataMessages');
      return false;
    }

    const database = await initializeDB();
    const testKey = ['test', 0] as [string, number];

    // Try to store
    await database.put(STORE_NAME, {
      recordingId: 'test',
      frameIndex: 0,
      frame,
      timestamp: Date.now(),
      accessCount: 1,
    });

    // Try to retrieve
    const result = await database.get(STORE_NAME, testKey);

    // Clean up test data
    await database.delete(STORE_NAME, testKey);

    // Check if the frame is still functional after round-trip
    const retrievedFrame = result?.frame;
    if (!retrievedFrame) {
      console.warn('Failed to retrieve frame from cache during test');
      return false;
    }

    // Validate retrieved frame structure (WASM frame structure)
    const retrievedFrameObj = retrievedFrame as unknown as Record<
      string,
      unknown
    >;
    const hasWasmStructure = Array.isArray(retrievedFrameObj.dataMessages);

    const isValid = !!(
      retrievedFrame &&
      hasWasmStructure &&
      typeof retrievedFrame.toString === 'function'
    );

    if (!isValid) {
      console.warn('Frame lost integrity during cache round-trip test');
    }

    return isValid;
  } catch (error) {
    console.warn('Frame caching test failed:', error);
    return false;
  }
}

const cachedIndicesCache = new Map<string, Set<number>>();

/**
 * Get array of cached frame indices for a recording.
 * Uses in-memory cache for performance.
 *
 * @param recordingId - The ID of the recording to get cached indices for
 * @returns Promise that resolves to an array of cached frame indices
 */
export async function getCachedFrameIndices(
  recordingId: string
): Promise<number[]> {
  // Return from memory cache if available
  const cached = cachedIndicesCache.get(recordingId);
  if (cached) {
    return Array.from(cached).sort((a, b) => a - b);
  }

  // If not in cache, build it once
  try {
    const database = await initializeDB();
    const range = IDBKeyRange.bound(
      [recordingId, 0],
      [recordingId, Number.MAX_SAFE_INTEGER]
    );

    const indices = new Set<number>();
    const tx = database.transaction(STORE_NAME, 'readonly');

    for await (const cursor of tx.store.iterate(range)) {
      if (cursor.value) {
        indices.add(cursor.value.frameIndex);
      }
    }

    await tx.done;

    // Cache the result
    cachedIndicesCache.set(recordingId, indices);

    return Array.from(indices).sort((a, b) => a - b);
  } catch (error) {
    console.warn('Failed to get cached frame indices:', error);
    return [];
  }
}

/**
 * Update the in-memory cache when a frame is stored.
 * Called internally by storeCachedFrame.
 *
 * @param recordingId - The ID of the recording
 * @param frameIndex - The index of the frame that was stored
 */
function updateCachedIndices(recordingId: string, frameIndex: number): void {
  const cached = cachedIndicesCache.get(recordingId);
  if (cached) {
    cached.add(frameIndex);
  }
}

/**
 * Clear the in-memory indices cache for a recording.
 *
 * @param recordingId - The ID of the recording to clear cache for
 */
function clearCachedIndices(recordingId: string): void {
  cachedIndicesCache.delete(recordingId);
}

/**
 * Set up automatic cache cleanup when the page/tab is closed.
 * Call this once during app initialization.
 *
 * @param recordingId - The ID of the recording to clean up cache for
 */
export function setupPageCloseCleanup(recordingId: string): () => void {
  // Historically this helper registered listeners itself, but that can be
  // problematic from different execution contexts (or when components want
  // to manage listeners directly). Keep this helper as a no-op that callers
  // can invoke safely. Real listeners should be registered from the
  // component (DriveViewer) so they run in the same window/tab context.
  console.debug('setupPageCloseCleanup called for', recordingId, '- no-op');
  return () => {
    // no-op
  };
}

/**
 * Release in-memory DB handles and caches to help free any retained
 * ArrayBuffer/typed-array clones that may have been created during
 * DB put/get operations. This is a best-effort debugging helper and
 * should not normally be required in production.
 */
export function releaseDbHandles(): void {
  try {
    // Clear in-memory indices cache
    cachedIndicesCache.clear();

    // Close the open IDB handle if present
    if (db) {
      try {
        db.close();
      } catch (err) {
        console.warn('Failed to close IDB handle:', err);
      }
      db = null;
    }
  } catch (error) {
    console.warn('Failed to release DB handles:', error);
  }
}
